import { OnboardingFooter } from "@/components/onboarding/OnboardingFooter"
import { OnboardingHero } from "@/components/onboarding/OnboardingHero"
import { OnboardingPanel } from "@/components/onboarding/OnboardingPanel"
import { Form } from "@/components/ui/form"

import type { FieldValues, UseFormReturn } from "react-hook-form"

interface Props<T extends FieldValues> {
  form: UseFormReturn<T>
  /** The handler `form.handleSubmit` returned. */
  onSubmit: React.FormEventHandler<HTMLFormElement>
  title: string
  description: string
  /** Text on the forward button. */
  label: string
  onBack: () => void
  /** The fields this step asks for. */
  children: React.ReactNode
}

/**
 * A step that asks questions: the hero, the copy, the fields and the buttons,
 * in the order every question step wants them. The dots below belong to the
 * wizard, which is what knows where the step sits in the run.
 *
 * The step itself is left with its schema, its fields and the one row it
 * writes. A save that fails goes through `applyErrorsToForm`: whatever the
 * server blames on a field lands under that field, and the rest arrives as the
 * form's root error, rendered here under the questions rather than over them.
 */
export function OnboardingFormStep<T extends FieldValues>({
  form,
  onSubmit,
  title,
  description,
  label,
  onBack,
  children,
}: Props<T>) {
  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <OnboardingHero badge="Onboarding" />
        <OnboardingPanel title={title} description={description}>
          <div className="flex flex-col gap-4">{children}</div>

          {form.formState.errors.root && (
            <p className="text-destructive mt-4 text-sm">
              {form.formState.errors.root.message}
            </p>
          )}
        </OnboardingPanel>
        <OnboardingFooter
          label={label}
          onBack={onBack}
          isSubmitting={form.formState.isSubmitting}
        />
      </form>
    </Form>
  )
}
